import React from 'react';
import Grid from '@mui/material/Grid';
import { useDecision } from '@optimizely/react-sdk';
import ProductId from '../../ProductId';
import useStyles from './styles';
import InfoRail from '../InfoRail';
import ProductInfoContent from './ProductInfoContent/ProductInfoContent';
import ProductImage from './ProductImage';
import ProductInfo from './ProductInfo';
import ProductDropdownsWithAddToCart from './ProductDropdownsWithAddToCart';
import RenderNutritionInfo from './RenderNutritionInfo';
import { LocalizedProductTileProps } from '@/common/components/LocalizedProductTile/LocalizedProductTile.types';
import { ButtonProps } from '@/coreComponents/buttons/SelectorButton/SelectorButton';
import useDisplayableProduct from '@/menu/categories/useDisplayableProduct';
import { replaceSpacesWithDash } from '@/common/replaceSpacesWithDash';
import { MELT } from '@/domain/constants';
import { checkSodiumWarning } from '@/clientCore/helper/checkSodiumWarning';

const defaultButtonDetails: ButtonProps = {
  ariaLabel: 'Add to Order',
  ariaLabelLoading: 'Loading add to order',
  text: 'Add',
  size: 'small',
  action: 'ADD_TO_CART'
};

const LocalizedProductTile = ({
  id,
  name,
  imageUrl,
  description,
  sizes,
  productIndex,
  additionalOptions,
  isOutOfStock,
  subcategory,
  sodiumWarning,
  categoryName,
  showQuantity = true,
  showPrice = true,
  buttonDetails,
  handleAddButtonClick,
  handleAddButtonClickV2,
  showErrorModal,
  telemetryEventName,
  handleAnalyticsForAddToCartClick,
  handleAnalyticsForAddToCartClickFromRail,
  handleAnalyticsForAddToCartSuccessFromRail,
  handleAnalyticsForModifierSelected,
  preSelectedAdditionalOption,
  preSelectedQuantity,
  preSelectedSize,
  isSingleStepDeal,
  oneClickDefaults,
  type,
  isMyHutBoxProduct = false,
  clickHandler,
  upcharge: upchargeAmount
}: LocalizedProductTileProps): JSX.Element => {
  const { values, setters, handlers } = useDisplayableProduct({
    imageUrl,
    name,
    sizes,
    subcategory,
    additionalOptions,
    categoryName,
    productIndex,
    showErrorModal,
    id,
    description,
    isOutOfStock,
    sodiumWarning,
    handleAddButtonClick,
    handleAddButtonClickV2,
    telemetryEventName,
    handleAnalyticsForAddToCartClick,
    handleAnalyticsForAddToCartClickFromRail,
    handleAnalyticsForAddToCartSuccessFromRail,
    handleAnalyticsForModifierSelected,
    userAction: buttonDetails?.ariaLabel ?? defaultButtonDetails.ariaLabel,
    preSelectedAdditionalOption,
    preSelectedQuantity,
    preSelectedSize,
    isSingleStepDeal,
    oneClickDefaults,
    type
  });

  const {
    quantity,
    selectedSize,
    optionsBySize,
    selectedAdditionalOption,
    hasMultipleSizes,
    hasAdditionalOptions,
    hasNutrition,
    formattedUnitPrice,
    buttonState,
    showInfoRail,
    lowerCaseCategory,
    isProductOutOfStock,
    isNew,
    isExcludedVariant,
    isMelt
  } = values;

  const isInStock = !isProductOutOfStock;

  const { updateSize, updateAdditionalOption, updateQuantity, setShowInfoRail } = setters;

  const { 
    handleAddToCart, 
    handleAddToCartV2, 
    handleAnalyticsForInfoRailSlideOut, 
    handleInfoButtonClick,
    handleCustomizeMelt 
  } = handlers;

  const classes = useStyles({});
  const { globalId: productId } = new ProductId(id);
  const classNameForGrid =
    hasMultipleSizes || hasAdditionalOptions
      ? `${classes.grid} ${classes.gridTemplateForDropdown}`
      : `${classes.grid} ${classes.gridTemplateWhenNoDropdown}`;
  const infoRailId = `product-info-rail-${name}`;
  const quantitySelectorInputLabel = `Quantity ${quantity} of ${name} selected.`;
  const btnDetails = { ...defaultButtonDetails, ...buttonDetails };
  const productNameWithoutSpaces = replaceSpacesWithDash(name);
  const isPizzaMelt = lowerCaseCategory === MELT.toLowerCase();
  const showSodiumWarning =
    sodiumWarning ||
    selectedAdditionalOption?.sodiumWarning ||
    selectedSize?.sodiumWarning ||
    checkSodiumWarning(oneClickDefaults ?? []);
  const [{ enabled: isAdditionalSizeUpchargeEnabled }] = useDecision('fr-dtg-1306-dispaly-menu-product-size-upcharge');
  const [{enabled: hidePriceForUHBEnabled}] = useDecision('fr-web-4676-hide-price-ultimate-hut-bundle');

  if(isExcludedVariant) {
    return <></>;
  }
  return (
    <Grid className={classNameForGrid} key={productId} data-testid="localized-product-tile-container">
      <ProductImage imageUrl={imageUrl} description={description} />
      <ProductInfo
        name={name}
        description={description}
        lowerCaseCategory={lowerCaseCategory}
        formattedUnitPrice={formattedUnitPrice}
        showPrice={hidePriceForUHBEnabled ? (isInStock && showPrice) : isInStock}
        sizes={sizes}
        categoryName={categoryName}
        handleInfoButtonClick={handleInfoButtonClick}
        sodiumWarning={showSodiumWarning}
        isProductOutOfStock={isProductOutOfStock}
        upcharge={upchargeAmount ?? 0}
        badges={{
          new: isNew && isInStock,
          upcharge: !!upchargeAmount,
          soldOut: isProductOutOfStock
        }}
      />
      {hasNutrition && isInStock && (
        <RenderNutritionInfo
          productId={productId}
          selectedSize={selectedSize}
          selectedAdditionalOption={selectedAdditionalOption}
        />
      )}
      {isInStock ? (
        <ProductDropdownsWithAddToCart
          id={id}
          productNameWithoutSpaces={productNameWithoutSpaces}
          buttonDetails={btnDetails}
          productName={name}
          quantity={quantity}
          showQuantity={showQuantity}
          hasMultipleSizes={hasMultipleSizes}
          hasAdditionalOptions={!isPizzaMelt && hasAdditionalOptions}
          formattedUnitPrice={formattedUnitPrice}
          sizes={sizes}
          selectedSize={selectedSize}
          updateSize={(e) => updateSize(e, false)}
          additionalOptions={optionsBySize}
          selectedAdditionalOption={selectedAdditionalOption}
          updateAdditionalOption={(e) => updateAdditionalOption(e, false)}
          handleAddToCart={(e, _, upcharge) =>
            isAdditionalSizeUpchargeEnabled
              ? handleAddToCartV2({
                  eventProps: e as unknown as React.MouseEvent<Element>,
                  fromProductInfoRail: true,
                  upcharge
                })
              : handleAddToCart(e as unknown as React.MouseEvent<Element>, true)
          }
          handleCustomizeMelt={handleCustomizeMelt}
          buttonState={buttonState}
          updateQuantity={updateQuantity}
          quantitySelectorInputLabel={quantitySelectorInputLabel}
          isMyHutBoxProduct={isMyHutBoxProduct}
          isMelt={isMelt}
          clickHandler={clickHandler}
        />
      ) : null}
      <InfoRail visible={showInfoRail} onClose={() => setShowInfoRail(false)} id={infoRailId}>
        <>
          <ProductInfoContent
            productNameWithoutSpaces={productNameWithoutSpaces}
            product={{
              id,
              name,
              description,
              sizes
            }}
            categoryName={categoryName}
            quantity={quantity}
            showQuantity={showQuantity}
            buttonState={buttonState}
            updateQuantity={updateQuantity}
            handleAddToCart={(e, _, upcharge) =>
              isAdditionalSizeUpchargeEnabled
                ? handleAddToCartV2({
                    eventProps: e as unknown as React.MouseEvent<Element>,
                    fromProductInfoRail: true,
                    upcharge
                  })
                : handleAddToCart(e as unknown as React.MouseEvent<Element>, true)
            }
            handleAnalyticsForInfoRailSlideOut={handleAnalyticsForInfoRailSlideOut}
            formattedUnitPrice={showPrice ? formattedUnitPrice : ''}
            hasMultipleSizes={hasMultipleSizes}
            hasAdditionalOptions={hasAdditionalOptions}
            selectedSize={selectedSize}
            updateSize={(e) => updateSize(e, true)}
            additionalOptions={optionsBySize}
            selectedAdditionalOption={selectedAdditionalOption}
            updateAdditionalOption={(e) => updateAdditionalOption(e, true)}
            productId={productId}
            hasNutrition={hasNutrition}
            quantitySelectorInputLabel={quantitySelectorInputLabel}
            buttonDetails={btnDetails}
            isMyHutBoxProduct={isMyHutBoxProduct}
            isProductOutOfStock={isProductOutOfStock}
          />
        </>
      </InfoRail>
    </Grid>
  );
};

export default LocalizedProductTile;
